/* Landing pages: scroll reveal, count-up stats, click-to-play video, MilkPal savings calculator. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  /* ---------- Reveal on scroll ---------- */
  const revealObserver = 'IntersectionObserver' in window && !reduceMotion && !designMode
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })
    : null;

  const initReveal = (root) => {
    root.querySelectorAll('.mp-reveal:not(.is-in), .mg-reveal:not(.is-in)').forEach((el) => {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-in');
    });
  };

  /* ---------- Count-up numbers ---------- */
  const formatLike = (template, value) => {
    const decimals = (template.split('.')[1] || '').replace(/\D/g, '').length;
    return value.toLocaleString('en-AU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const countObserver = 'IntersectionObserver' in window && !reduceMotion
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          countObserver.unobserve(entry.target);
          const el = entry.target;
          const raw = el.dataset.mpCount;
          const target = parseFloat(raw.replace(/[^\d.]/g, ''));
          if (Number.isNaN(target)) return;
          const prefix = raw.match(/^[^\d]*/)[0];
          const suffix = raw.match(/[^\d.]*$/)[0];
          const duration = 1400;
          const start = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 4);
            el.textContent = prefix + formatLike(raw, target * eased) + suffix;
            if (t < 1) requestAnimationFrame(tick);
            else el.textContent = raw;
          };
          requestAnimationFrame(tick);
        });
      }, { threshold: 0.4 })
    : null;

  const initCounters = (root) => {
    if (!countObserver || designMode) return;
    root.querySelectorAll('[data-mp-count]').forEach((el) => countObserver.observe(el));
  };

  /* ---------- Click-to-play video ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-mp-video]');
    if (!button) return;
    const { host, id, title } = button.dataset;
    if (!id) return;
    const iframe = document.createElement('iframe');
    iframe.title = title || 'Video';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.src = host === 'vimeo'
      ? `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1`
      : `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
    button.replaceWith(iframe);
    iframe.focus();
  });

  /* ---------- Savings calculator ---------- */
  if (!customElements.get('mp-calculator')) {
    customElements.define('mp-calculator', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.total = this.querySelector('[data-mp-calc-total]');
        this.hint = this.querySelector('[data-mp-calc-hint]');
        this.breakdown = this.querySelector('[data-mp-calc-breakdown]');
        this.defaultHint = this.hint ? this.hint.textContent : '';
        this.money = new Intl.NumberFormat(this.dataset.locale || 'en-AU', {
          style: 'currency',
          currency: this.dataset.currency || 'AUD',
          maximumFractionDigits: 0,
        });
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.calculate(true);
        });
        this.form.addEventListener('input', () => {
          if (this.hasResult) this.calculate(false);
        });
      }

      read(name) {
        const input = this.form.elements[name];
        const value = parseFloat(input.value);
        const valid = input.value !== '' && !Number.isNaN(value) && value >= 0;
        input.setAttribute('aria-invalid', String(!valid));
        return valid ? value : null;
      }

      calculate(fromSubmit) {
        const values = ['drinks', 'usage', 'waste', 'rate', 'days'].map((name) => this.read(name));
        if (values.includes(null)) {
          if (fromSubmit && this.hint) {
            this.hint.textContent = this.dataset.error;
            this.hint.classList.add('is-error');
            const firstInvalid = this.form.querySelector('[aria-invalid="true"]');
            if (firstInvalid) firstInvalid.focus();
          }
          return;
        }
        const [drinks, usage, waste, rate, days] = values;
        const milkCost = parseFloat(this.dataset.milkCost) || 0;
        const reduction = (parseFloat(this.dataset.wasteReduction) || 0) / 100;
        const secondsSaved = parseFloat(this.dataset.secondsSaved) || 0;

        const milkPerDay = usage * (Math.min(waste, 100) / 100) * reduction * milkCost;
        const hoursPerDay = (drinks * secondsSaved) / 3600;
        const timePerDay = hoursPerDay * rate;
        const annual = (milkPerDay + timePerDay) * days;

        this.hasResult = true;
        if (this.hint) {
          this.hint.textContent = this.defaultHint;
          this.hint.classList.remove('is-error');
        }
        this.animateTo(annual);
        this.breakdown.hidden = false;
        this.querySelector('[data-mp-calc-milk]').textContent = this.money.format(milkPerDay * days);
        this.querySelector('[data-mp-calc-time]').textContent = this.money.format(timePerDay * days);
        this.querySelector('[data-mp-calc-hours]').textContent = Math.round(hoursPerDay * days).toLocaleString('en-AU');
      }

      animateTo(value) {
        const from = this.current || 0;
        this.current = value;
        if (reduceMotion) {
          this.total.textContent = this.money.format(value);
          return;
        }
        const start = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - start) / 900);
          const eased = 1 - Math.pow(1 - t, 3);
          this.total.textContent = this.money.format(from + (value - from) * eased);
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    });
  }

  /* =======================================================================
     Gift page components
     ======================================================================= */

  /* ---------- Accessible tabs (milk modes, specs) ---------- */
  if (!customElements.get('mg-tabs')) {
    customElements.define('mg-tabs', class extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[role="tab"]')];
        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.select(index, false));
          tab.addEventListener('keydown', (event) => {
            const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
            if (event.key in keys) {
              event.preventDefault();
              this.select((index + keys[event.key] + this.tabs.length) % this.tabs.length, true);
            } else if (event.key === 'Home') { event.preventDefault(); this.select(0, true); }
            else if (event.key === 'End') { event.preventDefault(); this.select(this.tabs.length - 1, true); }
          });
        });
      }

      select(index, focus) {
        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          const panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });
        const scope = this.closest('section') || this;
        scope.querySelectorAll('[data-panel-index]').forEach((el) => { el.hidden = Number(el.dataset.panelIndex) !== index; });
        const tone = this.tabs[index].dataset.tone;
        if (tone) this.dataset.tone = tone;
        if (focus) this.tabs[index].focus();
      }
    });
  }

  /* ---------- Mode panels: click, hover or focus makes a panel active ---------- */
  if (!customElements.get('mg-panels')) {
    customElements.define('mg-panels', class extends HTMLElement {
      connectedCallback() {
        const panels = [...this.querySelectorAll('.mg-panel')];
        const activate = (panel) => panels.forEach((el) => el.classList.toggle('is-active', el === panel));
        panels.forEach((panel) => {
          panel.addEventListener('click', () => activate(panel));
          panel.addEventListener('focus', () => activate(panel));
          panel.addEventListener('keydown', (event) => {
            if (event.target !== panel) return;
            const i = panels.indexOf(panel);
            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') { event.preventDefault(); panels[(i + 1) % panels.length].focus(); }
            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') { event.preventDefault(); panels[(i - 1 + panels.length) % panels.length].focus(); }
          });
        });
      }
    });
  }

  /* ---------- Cart: one source for the count shown in the header and the bar ---------- */
  const shopRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const refreshCart = async () => {
    try {
      const cart = await (await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } })).json();
      document.dispatchEvent(new CustomEvent('mg:cart', { detail: { count: cart.item_count, cart } }));
      return cart;
    } catch (error) { return null; }
  };
  document.addEventListener('mg:cart', (event) => {
    document.querySelectorAll('[data-mg-cart-count]').forEach((el) => {
      const grew = event.detail.count > (Number(el.textContent) || 0) && !el.hidden;
      el.textContent = event.detail.count; el.hidden = !event.detail.count;
      if (grew || (event.detail.count && el.dataset.seen && el.dataset.seen !== String(event.detail.count))) { el.classList.remove('is-bump'); void el.offsetWidth; el.classList.add('is-bump'); }
      el.dataset.seen = String(event.detail.count);
      const link = el.closest('[data-cart-label]');
      if (link) link.setAttribute('aria-label', event.detail.count ? `${link.dataset.cartLabel} (${event.detail.count})` : link.dataset.cartLabel);
    });
  });

  /* ---------- Pick a model card from anywhere (fit finder, calculator, phone switcher) ---------- */
  const selectPanel = (panel, { match, scroll = true, focus = true } = {}) => {
    if (!panel) return;
    const panels = [...panel.parentElement.querySelectorAll('.mg-panel')];
    panels.forEach((el) => {
      el.classList.toggle('is-active', el === panel);
      if (el === panel) el.setAttribute('data-current', ''); else el.removeAttribute('data-current');
    });
    const section = panel.closest('section');
    if (section) section.querySelectorAll('[data-mg-switch-to]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mgSwitchTo === String(panels.indexOf(panel)))));
    if (match) {
      const want = match.toLowerCase();
      const radio = [...panel.querySelectorAll('.mg-seg input')].find((i) => i.value.toLowerCase().includes(want));
      if (radio && !radio.checked) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
      const option = [...panel.querySelectorAll('select[data-mg-option] option')].find((o) => o.value.toLowerCase().includes(want));
      if (option && !option.selected) { option.selected = true; option.parentElement.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    if (scroll) panel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    panel.classList.remove('is-picked'); void panel.offsetWidth; panel.classList.add('is-picked');
    const buy = panel.querySelector('[data-mg-add]');
    if (focus && buy) setTimeout(() => buy.focus({ preventScroll: true }), reduceMotion ? 0 : 450);
  };

  /* ---------- Phone model switcher: one buy box at a time under 700px ---------- */
  if (!customElements.get('mg-switch')) {
    customElements.define('mg-switch', class extends HTMLElement {
      connectedCallback() {
        const panels = this.closest('section').querySelector('.mg-panels');
        if (!panels) return;
        panels.setAttribute('data-switch-ready', '');
        this.querySelectorAll('[data-mg-switch-to]').forEach((btn) => btn.addEventListener('click', () => {
          selectPanel(panels.querySelectorAll('.mg-panel')[Number(btn.dataset.mgSwitchTo)], { scroll: false, focus: false });
        }));
      }
    });
  }

  /* ---------- Panel buy box: options pick the variant; Buy now goes to checkout, Add to cart stays ---------- */
  if (!customElements.get('mg-buy')) {
    customElements.define('mg-buy', class extends HTMLElement {
      connectedCallback() {
        const form = this.querySelector('form');
        const idInput = this.querySelector('[data-mg-id]');
        const qtyInput = this.querySelector('input[name="quantity"]');
        const optionEls = [...this.querySelectorAll('[data-mg-option]')];
        const buttons = [...this.querySelectorAll('[data-mg-add]')];
        const primary = buttons[0];
        const price = this.querySelector('[data-mg-price]');
        const added = this.querySelector('[data-mg-added]');
        const error = this.querySelector('[data-mg-error]');
        const image = this.closest('.mg-panel')?.querySelector('[data-mg-image]');
        const ctas = this.querySelector('.mg-panel__ctas');
        let variants = [];
        try { variants = JSON.parse(this.querySelector('[data-mg-variants]').textContent); } catch (err) { return; }
        const valueOf = (el) => (el.tagName === 'SELECT' ? el.value : (el.querySelector('input:checked') || {}).value);

        const update = () => {
          const current = variants.find((v) => String(v.id) === idInput.value);
          const wanted = current ? [...current.options] : [];
          optionEls.forEach((el) => { wanted[Number(el.dataset.mgOption)] = valueOf(el); });
          const match = variants.find((v) => v.options.every((value, i) => value === wanted[i]));
          if (match) {
            idInput.value = match.id;
            price.textContent = match.price;
            const was = price.closest('.mg-panel__price')?.querySelector('[data-mg-was]');
            if (was) { was.hidden = !match.was; if (match.was) was.textContent = match.was; }
            price.closest('.mg-panel__price')?.classList.remove('is-unavailable');
            if (image && match.image) { image.removeAttribute('srcset'); image.src = match.image; }
          } else {
            price.closest('.mg-panel__price')?.classList.add('is-unavailable');
          }
          const ok = Boolean(match && match.available);
          buttons.forEach((b) => { b.disabled = !ok; b.removeAttribute('aria-busy'); delete b.dataset.state; b.textContent = b.dataset.label; });
          if (!ok) primary.textContent = match ? primary.dataset.soldOut : primary.dataset.unavailable;
          if (added) added.hidden = true;
          if (ctas) ctas.classList.remove('is-added');
          if (error) error.hidden = true;
        };
        optionEls.forEach((el) => el.addEventListener('change', update));
        // Browsers restore picked options on Back; keep id, price and photo in step with what is shown
        window.addEventListener('pageshow', update);
        update();

        form.addEventListener('submit', async (event) => {
          if (!window.fetch) return;
          event.preventDefault();
          const submitter = event.submitter && event.submitter.matches('[data-mg-add]') ? event.submitter : primary;
          const buyNow = submitter.value === '/checkout';
          const id = Number(idInput.value);
          buttons.forEach((b) => { b.disabled = true; });
          submitter.setAttribute('aria-busy', 'true');
          if (error) error.hidden = true;
          try {
            if (buyNow) {
              const cart = await refreshCart();
              if (cart && cart.items.some((item) => item.variant_id === id)) { window.location.href = `${shopRoot()}checkout`; return; }
            }
            const response = await fetch(`${shopRoot()}cart/add.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id, quantity: Number(qtyInput ? qtyInput.value : 1) || 1 }] }),
            });
            if (response.status === 422 || response.status === 400) {
              const data = await response.json().catch(() => ({}));
              update();
              if (error) { error.textContent = data.description || data.message || ''; error.hidden = !error.textContent; }
              submitter.focus({ preventScroll: true });
              return;
            }
            if (!response.ok) throw new Error(String(response.status));
            if (buyNow) { window.location.href = `${shopRoot()}checkout`; return; }
            await refreshCart();
            submitter.removeAttribute('aria-busy');
            buttons.forEach((b) => { b.disabled = false; });
            if (added) {
              // The buttons give way to the confirmation: Checkout becomes the one next step
              if (ctas && ctas.contains(added)) ctas.classList.add('is-added');
              added.hidden = false;
              const next = added.querySelector('a');
              if (next) next.focus({ preventScroll: true });
            } else {
              submitter.textContent = submitter.dataset.added || submitter.dataset.label;
              setTimeout(() => { submitter.textContent = submitter.dataset.label; }, 2500);
            }
          } catch (err) {
            if (buyNow && !form.querySelector('input[name="return_to"]')) {
              const returnTo = document.createElement('input');
              returnTo.type = 'hidden'; returnTo.name = 'return_to'; returnTo.value = '/checkout';
              form.appendChild(returnTo);
            }
            form.submit();
          }
        });
      }
    });
  }

  /* ---------- Product tour: numbered pins and their list highlight together ---------- */
  if (!customElements.get('mg-anatomy')) {
    customElements.define('mg-anatomy', class extends HTMLElement {
      connectedCallback() {
        const pins = [...this.querySelectorAll('.mg-anatomy__pin')];
        const items = [...this.querySelectorAll('.mg-anatomy__item')];
        const show = (i, fromPin) => {
          pins.forEach((pin, n) => { pin.classList.toggle('is-active', n === i); pin.setAttribute('aria-pressed', String(n === i)); });
          items.forEach((item, n) => item.classList.toggle('is-active', n === i));
          if (fromPin && window.matchMedia('(max-width: 989px)').matches && items[i]) items[i].scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
        };
        pins.forEach((pin, i) => {
          pin.addEventListener('click', () => show(i, true));
          pin.addEventListener('mouseenter', () => show(i));
          pin.addEventListener('focus', () => show(i));
        });
        items.forEach((item, i) => {
          item.addEventListener('mouseenter', () => show(i));
          item.addEventListener('click', () => show(i));
        });
        show(0);
        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver((entries) => entries.forEach((e) => { if (e.isIntersecting) { this.classList.add('is-in'); io.disconnect(); } }), { threshold: 0.3 });
          io.observe(this);
        } else this.classList.add('is-in');
      }
    });
  }

  /* ---------- Fit finder: an answer picks the matching mode panel ---------- */
  if (!customElements.get('mg-finder')) {
    customElements.define('mg-finder', class extends HTMLElement {
      connectedCallback() {
        const section = this.closest('section');
        const result = this.querySelector('[data-result]');
        this.querySelectorAll('[data-answer]').forEach((btn) => btn.addEventListener('click', () => {
          const answer = btn.dataset.answer;
          const panel = section.querySelector(`.mg-panel[data-fit="${answer === 'yes' ? 'yes' : 'no'}"]`);
          if (!panel) return;
          this.querySelectorAll('[data-answer]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
          const name = panel.dataset.name;
          result.querySelector('[data-result-name]').textContent = name;
          result.querySelector('[data-result-text]').textContent = answer === 'unsure' ? this.dataset.unsure : panel.dataset.fitText;
          result.querySelector('[data-result-cta]').textContent = (this.dataset.cta || 'Book a {name} demo').replace('{name}', name);
          const buyBtn = result.querySelector('[data-result-buy]');
          if (buyBtn) { buyBtn.textContent = (this.dataset.buyCta || 'Buy the {name}').replace('{name}', name); buyBtn.onclick = () => selectPanel(panel); }
          result.hidden = false;
          result.classList.remove('is-shown');
          requestAnimationFrame(() => result.classList.add('is-shown'));
          section.querySelectorAll('.mg-panel').forEach((el) => el.classList.toggle('is-active', el === panel));
          if (panel.parentElement.hasAttribute('data-switch-ready')) selectPanel(panel, { scroll: false, focus: false });
          panel.classList.remove('is-picked');
          void panel.offsetWidth;
          panel.classList.add('is-picked');
          const row = panel.parentElement;
          if (row.scrollWidth > row.clientWidth) row.scrollTo({ left: panel.offsetLeft - row.children[0].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
          document.querySelectorAll('[data-mg-interest]').forEach((input) => { input.value = name; });
        }));
      }
    });
  }

  /* ---------- Enquiry buttons tell the contact form which product the visitor asked about ---------- */
  document.addEventListener('click', (event) => {
    const el = event.target.closest('[data-mg-enquire]');
    if (el) document.querySelectorAll('[data-mg-interest]').forEach((input) => { input.value = el.dataset.mgEnquire; });
  });

  /* ---------- Speed race: two lanes fill against a shared clock ---------- */
  if (!customElements.get('mg-race')) {
    customElements.define('mg-race', class extends HTMLElement {
      connectedCallback() {
        if (reduceMotion || designMode || !('IntersectionObserver' in window)) { this.classList.add('is-finished'); return; }
        this.lanes = [...this.querySelectorAll('[data-lane]')].map((lane) => ({
          el: lane,
          total: Number(this.dataset[lane.dataset.lane]),
          fill: lane.querySelector('[data-fill]'),
          time: lane.querySelector('[data-time]'),
        }));
        this.max = Math.max(...this.lanes.map((l) => l.total));
        this.classList.add('is-ready');
        this.lanes.forEach((l) => { l.fill.style.width = '0%'; l.time.textContent = this.dataset.format === 'hours' ? '0 min' : '0:00'; });
        const io = new IntersectionObserver((entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          io.disconnect();
          setTimeout(() => this.run(), 350);
        }, { threshold: 0.5 });
        io.observe(this);
      }
      run() {
        const ms = Number(this.dataset.duration || 4.5) * 1000;
        const start = performance.now();
        const clockFmt = (sec) => `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;
        const hoursFmt = (sec) => {
          const h = Math.floor(sec / 3600); const m = Math.floor((sec % 3600) / 60);
          if (h && m) return `${h} h ${m} min`;
          return h ? `${h} h` : `${m} min`;
        };
        const fmt = this.dataset.format === 'hours' ? hoursFmt : clockFmt;
        const tick = (now) => {
          const clock = Math.min(1, (now - start) / ms) * this.max;
          this.lanes.forEach((l) => {
            const t = Math.min(clock, l.total);
            l.fill.style.width = `${(t / this.max) * 100}%`;
            l.time.textContent = fmt(t);
            if (t >= l.total) l.el.classList.add('is-done');
          });
          if (clock < this.max) requestAnimationFrame(tick);
          else this.classList.add('is-finished');
        };
        requestAnimationFrame(tick);
      }
    });
  }

  /* ---------- Cold brew planner: drinks a day picks the machine, then weekly profit and payback ---------- */
  if (!customElements.get('mg-planner')) {
    customElements.define('mg-planner', class extends HTMLElement {
      connectedCallback() {
        this.money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
        this.whole = new Intl.NumberFormat('en-AU');
        this.inputs = [...this.querySelectorAll('input[type="range"]')];
        this.nitro = this.querySelector('[data-plan-nitro]');
        this.machines = [...this.querySelectorAll('[data-plan-machine]')].map((t) => ({ ...t.dataset, max: Number(t.dataset.max), batch: Number(t.dataset.batch), minutes: Number(t.dataset.minutes), price: Number(t.dataset.price), index: Number(t.dataset.index) }));
        this.inputs.forEach((input) => input.addEventListener('input', () => this.update()));
        if (this.nitro) this.nitro.addEventListener('change', () => this.update());
        // On phones the sticky summary repeats the result card, so it steps aside while the card is on screen.
        const card = this.querySelector('.mg-plan__card');
        if (card && 'IntersectionObserver' in window) {
          new IntersectionObserver((entries) => entries.forEach((e) => this.classList.toggle('is-card-visible', e.isIntersecting)), { rootMargin: '0px 0px -10% 0px' }).observe(card);
          // ...and while the drinks slider itself is near the top, so it never covers the value being set.
          const volume = this.querySelector('.mg-plan__volume');
          if (volume) new IntersectionObserver((entries) => entries.forEach((e) => this.classList.toggle('is-volume-top', e.isIntersecting)), { rootMargin: '0px 0px -60% 0px' }).observe(volume);
        }
        this.buy = this.querySelector('[data-plan-buy]');
        if (this.buy) this.buy.addEventListener('click', () => {
          const m = this.machine;
          if (this.dataset.ctaLink) {
            const nitro = this.querySelector('[data-plan-nitro]');
            const name = m ? m.name + (nitro && nitro.checked ? ' + Hardtap' : '') : '';
            document.querySelectorAll('[data-mg-interest]').forEach((input) => { input.value = name; });
            const target = this.dataset.ctaLink.startsWith('#') ? document.querySelector(this.dataset.ctaLink) : null;
            if (target) target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
            else window.location.href = this.dataset.ctaLink;
            return;
          }
          const panel = m && m.handle ? document.querySelector(`.mg-panel[data-product-handle="${m.handle}"]`) : null;
          if (panel) selectPanel(panel, { match: m.match });
          else { const contact = document.getElementById('mg-contact'); if (contact) contact.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' }); }
        });
        this.update();
      }
      value(name) { const input = this.inputs.find((i) => i.name === name); return input ? parseFloat(input.value) : 0; }
      update() {
        this.inputs.forEach((input) => {
          const min = parseFloat(input.min); const max = parseFloat(input.max); const v = parseFloat(input.value);
          input.style.setProperty('--p', `${((v - min) / (max - min)) * 100}%`);
          const out = input.closest('.mg-slider').querySelector('[data-slider-output] span');
          const text = input.step && input.step.includes('.') ? v.toFixed(2).replace(/\.00$/, '') : this.whole.format(v);
          if (out) out.textContent = text;
          input.setAttribute('aria-valuetext', `${text}`);
        });
        if (!this.machines.length) return;
        const drinks = this.value('drinks');
        const over = drinks > this.machines[this.machines.length - 1].max;
        const m = this.machines.find((x) => drinks <= x.max) || this.machines[this.machines.length - 1];
        this.machine = m;
        const nitroOn = Boolean(this.nitro && this.nitro.checked);
        const serve = Number(this.dataset.serve) || 200;
        const perBatch = Math.max(1, Math.floor((m.batch * 1000) / serve));
        const batches = Math.ceil(Math.min(drinks, m.max) / perBatch);
        // Margin only counts drinks the chosen machine can make; above that the planner points to the 'two machines' note.
        const weekly = Math.max(0, (this.value('price') - this.value('cost')) * Math.min(drinks, m.max) * this.value('days'));
        const setup = m.price + (nitroOn ? Number(this.dataset.nitroPrice) || 0 : 0);
        const weeks = weekly > 0 ? setup / weekly : Infinity;
        const set = (sel, text) => { this.querySelectorAll(sel).forEach((el) => { el.textContent = text; }); };
        set('[data-plan-name]', m.name);
        const fitLine = (this.dataset.fitLabel || '').replace('{drinks}', this.whole.format(Math.min(drinks, m.max))).replace('{max}', this.whole.format(m.max));
        set('[data-plan-fit]', [m.fit, fitLine].filter(Boolean).join('. '));
        const meter = this.querySelector('[data-plan-meter]');
        if (meter) meter.style.width = `${Math.min(100, (drinks / m.max) * 100)}%`;
        this.querySelectorAll('[data-plan-img]').forEach((img) => img.classList.toggle('is-on', Number(img.dataset.planImg) === m.index));
        this.querySelectorAll('[data-zone]').forEach((z) => z.classList.toggle('is-on', over ? z.dataset.zone === 'over' : z.dataset.zone === String(m.index)));
        const plus = this.querySelector('[data-plan-plus]'); if (plus) plus.hidden = !nitroOn;
        const overEl = this.querySelector('[data-plan-over]'); if (overEl) overEl.classList.toggle('is-on', over);
        set('[data-plan-week]', this.money.format(weekly));
        set('[data-plan-batches]', `${batches}`);
        const bd = this.querySelector('[data-plan-batches]');
        if (bd && m.minutes) bd.title = (this.dataset.batchesLabel || '').replace('{n}', m.minutes);
        set('[data-plan-setup]', this.money.format(setup));
        set('[data-plan-litres]', `${m.batch} L`);
        set('[data-plan-daily]', this.whole.format(m.max));
        let payback = 'N/A';
        if (Number.isFinite(weeks)) payback = weeks < 1 ? (this.dataset.underLabel || '< 1') : `${Math.ceil(weeks)} ${this.dataset.weeksLabel || 'weeks'}`;
        set('[data-plan-payback]', payback);
        if (this.buy) this.buy.textContent = (this.dataset.buyLabel || '{name}').replace('{name}', m.name);
      }
    });
  }

  /* ---------- Scroll-linked "how it works" ---------- */
  if (!customElements.get('mg-steps')) {
    customElements.define('mg-steps', class extends HTMLElement {
      connectedCallback() {
        this.steps = [...this.querySelectorAll('.mg-step')];
        this.progress = this.querySelector('[data-mg-step-progress]');
        this.current = this.querySelector('[data-mg-step-current]');
        this.track = this.querySelector('.mg-steps__track');
        this.onScroll = () => { if (!this.ticking) { this.ticking = true; requestAnimationFrame(() => { this.update(); this.ticking = false; }); } };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.update();
      }
      disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
      update() {
        const focusLine = window.innerHeight * 0.55;
        let active = 0;
        this.steps.forEach((step, i) => { if (step.getBoundingClientRect().top < focusLine) active = i; });
        this.steps.forEach((step, i) => {
          step.classList.toggle('is-active', i === active);
          step.classList.toggle('is-done', i < active);
        });
        if (this.current) this.current.textContent = this.steps[active] ? this.steps[active].dataset.mgStep : '01';
        if (this.progress && this.track) {
          const r = this.track.getBoundingClientRect();
          const pct = Math.max(0, Math.min(1, (focusLine - r.top) / r.height));
          this.progress.style.setProperty('--progress', pct.toFixed(3));
        }
      }
    });
  }

  /* ---------- Live savings calculator with sliders ---------- */
  if (!customElements.get('mg-roi')) {
    customElements.define('mg-roi', class extends HTMLElement {
      connectedCallback() {
        this.inputs = [...this.querySelectorAll('input[type="range"]')];
        this.money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: this.dataset.currency || 'AUD', maximumFractionDigits: 0 });
        this.inputs.forEach((input) => input.addEventListener('input', () => this.update()));
        this.chips = [...this.querySelectorAll('[data-roi-model]')];
        this.userPicked = false;
        this.chips.forEach((chip) => chip.addEventListener('click', () => { this.userPicked = true; this.pick(chip); this.update(); }));
        const buy = this.querySelector('[data-roi-buy]');
        if (buy) buy.addEventListener('click', () => {
          const chip = this.current();
          if (!chip) return;
          const panel = document.querySelector(`.mg-panel[data-product-handle="${chip.dataset.handle}"]`);
          if (panel) selectPanel(panel, { match: chip.dataset.match });
          else { const modes = document.getElementById('mg-modes'); if (modes) modes.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' }); }
        });
        this.update(true);
      }
      current() { return this.chips.find((c) => c.getAttribute('aria-pressed') === 'true') || this.chips[0]; }
      pick(chip) { this.chips.forEach((c) => c.setAttribute('aria-pressed', String(c === chip))); }
      fill(text, map) { return Object.keys(map).reduce((t, k) => t.split(`{${k}}`).join(map[k]), text || ''); }
      value(name) {
        const input = this.inputs.find((i) => i.name === name);
        return input ? parseFloat(input.value) : 0;
      }
      update(initial) {
        this.inputs.forEach((input) => {
          const min = parseFloat(input.min); const max = parseFloat(input.max);
          input.style.setProperty('--p', `${((parseFloat(input.value) - min) / (max - min)) * 100}%`);
          const out = input.closest('.mg-slider').querySelector('[data-slider-output] span');
          if (out) out.textContent = Number(input.value).toLocaleString('en-AU');
          const valueText = input.closest('.mg-slider').querySelector('[data-slider-output]').textContent.replace(/\s+/g, ' ').trim();
          input.setAttribute('aria-valuetext', valueText);
        });
        const milkCost = parseFloat(this.dataset.milkCost) || 0;
        const reduction = (parseFloat(this.dataset.wasteReduction) || 0) / 100;
        const seconds = parseFloat(this.dataset.secondsSaved) || 0;
        const price = parseFloat(this.dataset.price) || 0;
        const weekly = this.dataset.mode === 'weekly';
        const days = weekly ? this.value('days') * 52 : this.value('days');
        let milk;
        if (!weekly) milk = this.value('usage') * (this.value('waste') / 100) * reduction * milkCost * days;
        else if (this.dataset.basis === 'shots_per_day') milk = this.value('waste') * reduction * ((parseFloat(this.dataset.dose) || 0) / 1000) * milkCost * days;
        else milk = this.value('drinks') * (this.value('waste') / 1000) * reduction * milkCost * days;
        const hours = (this.value('drinks') * seconds / 3600) * days;
        const time = hours * this.value('rate');
        const total = milk + time;

        this.tween(this.querySelector('[data-roi-total]'), total, initial);
        this.querySelector('[data-roi-milk]').textContent = this.money.format(milk);
        this.querySelector('[data-roi-time]').textContent = this.money.format(time);
        this.querySelector('[data-roi-hours]').textContent = Math.round(hours).toLocaleString('en-AU');
        const milkShare = total > 0 ? (milk / total) * 100 : 50;
        const bar = this.querySelector('[data-roi-bar-milk]');
        if (bar) bar.style.setProperty('--share', (milkShare / 100).toFixed(3));

        const payback = this.querySelector('[data-roi-payback]');
        if (weekly) {
          const perWeek = total / 52;
          const weekLine = this.querySelector('[data-roi-weekly]');
          if (weekLine) weekLine.textContent = this.fill(this.dataset.weekLabel, { saving_week: this.money.format(perWeek) });
          // Coffee volume decides the smallest model that copes; only until the visitor picks one
          const kg = this.dataset.basis === 'shots_per_day' ? this.value('drinks') * this.value('days') * ((parseFloat(this.dataset.dose) || 0) / 1000) : 0;
          const fits = (c) => !c.dataset.maxKg || kg <= parseFloat(c.dataset.maxKg);
          if (kg && !this.userPicked && this.chips.length && !fits(this.current())) this.pick(this.chips.find(fits) || this.chips[this.chips.length - 1]);
          const chip = this.current();
          const chipPrice = chip ? parseFloat(chip.dataset.price) || 0 : price;
          payback.textContent = chipPrice > 0 && perWeek > 0 ? this.fill(this.dataset.weeksLabel, { weeks: Math.ceil(chipPrice / perWeek) }) : 'N/A';
          const buy = this.querySelector('[data-roi-buy]');
          if (buy && chip) buy.textContent = this.fill(this.dataset.buyLabel, { model: chip.dataset.label });
          const note = this.querySelector('[data-roi-volume]');
          if (note) {
            const over = chip && kg && !fits(chip);
            const suggest = this.chips.find(fits);
            note.hidden = !(over && suggest);
            if (over && suggest) note.textContent = this.fill(this.dataset.volumeNote, { kg: Math.round(kg * 10) / 10, suggest: suggest.dataset.label, model: chip.dataset.label, max: chip.dataset.maxKg });
          }
        } else if (price > 0 && total > 0) {
          const months = (price / total) * 12;
          payback.textContent = months < 1 ? this.dataset.underLabel : `~${months < 10 ? months.toFixed(1) : Math.round(months)} ${this.dataset.monthsLabel}`;
        } else {
          payback.textContent = 'N/A';
        }
      }
      tween(el, value, instant) {
        const from = this.last || 0;
        this.last = value;
        if (instant || reduceMotion) { el.textContent = this.money.format(value); return; }
        cancelAnimationFrame(this.raf);
        const start = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - start) / 450);
          el.textContent = this.money.format(from + (value - from) * (1 - Math.pow(1 - t, 3)));
          if (t < 1) this.raf = requestAnimationFrame(step);
        };
        this.raf = requestAnimationFrame(step);
      }
    });
  }

  /* ---------- Video in a modal dialog ---------- */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-mg-modal-video]');
    if (!trigger || !trigger.dataset.id) return;
    const { host, id, title } = trigger.dataset;
    const dialog = document.createElement('dialog');
    dialog.className = 'mg-modal';
    dialog.setAttribute('aria-label', title || 'Video');
    const src = host === 'vimeo'
      ? `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1`
      : `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
    dialog.innerHTML = '<button type="button" class="mg-modal__close" aria-label="Close video">×</button><div class="mg-modal__frame"></div>';
    const frame = document.createElement('iframe');
    frame.src = src;
    frame.title = title || 'Video';
    frame.allow = 'autoplay; encrypted-media; picture-in-picture';
    frame.allowFullscreen = true;
    dialog.querySelector('.mg-modal__frame').appendChild(frame);
    document.body.appendChild(dialog);
    const close = () => { dialog.close(); };
    dialog.querySelector('.mg-modal__close').addEventListener('click', close);
    dialog.addEventListener('click', (e) => { if (e.target === dialog) close(); });
    dialog.addEventListener('close', () => { dialog.remove(); trigger.focus(); });
    dialog.showModal();
  });

  /* ---------- Sticky nav state, active link, mobile bar ---------- */
  const initNav = () => {
    const nav = document.querySelector('[data-mg-nav]');
    const bar = document.querySelector('[data-mg-bar]');
    if (!nav && !bar) return;
    const hero = document.getElementById('mg-hero');
    const contact = document.getElementById('mg-contact');
    const cards = bar && bar.hasAttribute('data-hide-over-cards') ? (document.getElementById('mg-modes') || document.getElementById('mg-buy')) : null;
    if (bar && bar.dataset.cartLabel) {
      const name = bar.querySelector('[data-mg-bar-name]');
      const button = bar.querySelector('[data-mg-bar-button]');
      const original = { name: name ? name.textContent : '', label: button.textContent, href: button.getAttribute('href') };
      document.addEventListener('mg:cart', (event) => {
        const count = event.detail.count;
        bar.classList.toggle('has-cart', count > 0);
        if (name) name.textContent = count > 0 ? `${bar.dataset.cartLabel} (${count})` : original.name;
        button.textContent = count > 0 ? bar.dataset.checkoutLabel : original.label;
        button.setAttribute('href', count > 0 ? `${shopRoot()}checkout` : original.href);
      });
    }
    const links = nav ? [...nav.querySelectorAll('.mg-nav__links a[href^="#"]')] : [];
    const targets = links.map((a) => document.querySelector(a.getAttribute('href'))).filter(Boolean);
    const onScroll = () => {
      const y = window.scrollY;
      if (nav) nav.classList.toggle('is-scrolled', y > 10);
      if (bar) {
        const pastHero = hero ? hero.getBoundingClientRect().bottom < 0 : y > 600;
        const atContact = contact ? contact.getBoundingClientRect().top < window.innerHeight : false;
        let overCards = false;
        if (cards) {
          const r = cards.getBoundingClientRect();
          overCards = r.top < window.innerHeight * 0.85 && r.bottom > window.innerHeight * 0.15;
        }
        const show = pastHero && !atContact && !overCards;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        bar.inert = !show;
        bar.querySelectorAll('a').forEach((a) => { a.tabIndex = show ? 0 : -1; });
      }
      let current = null;
      targets.forEach((t) => { if (t.getBoundingClientRect().top < window.innerHeight * 0.4) current = t; });
      links.forEach((a) => a.classList.toggle('is-active', current && a.getAttribute('href') === `#${current.id}`));
    };
    let queued = false;
    window.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; onScroll(); });
    }, { passive: true });
    onScroll();
  };

  /* ---------- Live sticky-header height (anchors + sticky columns stay aligned) ---------- */
  const initStickyOffset = () => {
    const header = document.querySelector('.mp-header--sticky, [data-mg-nav]');
    const set = () => document.documentElement.style.setProperty('--mp-sticky-h', `${header ? Math.round(header.getBoundingClientRect().height) : 0}px`);
    set();
    if (header && 'ResizeObserver' in window) {
      const ro = new ResizeObserver(set);
      ro.observe(header);
      header.querySelectorAll('[data-mg-bar] > div').forEach((el) => ro.observe(el));
    }
  };

  /* ---------- Smooth FAQ accordion ---------- */
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('.mp-faq__q');
    if (!summary || reduceMotion) return;
    const details = summary.parentElement;
    const panel = details.querySelector('.mp-faq__a');
    if (!panel || !panel.animate) return;
    event.preventDefault();
    if (details._anim) details._anim.cancel();
    const closing = details.open && !details.classList.contains('is-closing');
    // A closed <details> can still report its hidden content's height, so start from 0 when closed
    const start = details.open ? panel.getBoundingClientRect().height : 0;
    const pad = parseFloat(getComputedStyle(panel).paddingBottom) || 0;
    panel.classList.add('is-animating');
    let end;
    if (closing) {
      details.classList.add('is-closing');
      end = 0;
    } else {
      details.classList.remove('is-closing');
      details.open = true;
      end = panel.scrollHeight;
    }
    const anim = panel.animate(
      [
        { height: `${start}px`, paddingBottom: closing || start > 0 ? `${pad}px` : '0px', opacity: closing || start > 0 ? 1 : 0 },
        { height: `${end}px`, paddingBottom: closing ? '0px' : `${pad}px`, opacity: closing ? 0 : 1 },
      ],
      { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );
    details._anim = anim;
    anim.onfinish = () => {
      if (closing) { details.open = false; details.classList.remove('is-closing'); }
      panel.classList.remove('is-animating');
      details._anim = null;
    };
  });

  /* ---------- Spec sheets: always open on desktop, tap-to-open on mobile ---------- */
  const initSpecAccordions = (root = document) => {
    const mobile = window.matchMedia('(max-width: 699px)');
    root.querySelectorAll('.mg-acc').forEach((el) => {
      const summary = el.querySelector('summary');
      if (!summary || el.dataset.ready) return;
      el.dataset.ready = 'true';
      if (mobile.matches && !el.hasAttribute('data-keep-open')) el.open = false;
      if (!mobile.matches) summary.tabIndex = -1;
      summary.addEventListener('click', (event) => { if (!mobile.matches) event.preventDefault(); });
    });
  };

  /* ---------- Boot + theme editor support ---------- */
  const init = (root = document) => {
    initReveal(root);
    initCounters(root);
  };

  /* ---------- Hero background video: silent YouTube loop, added after the page has loaded ---------- */
  const initBgVideo = () => {
    const holders = document.querySelectorAll('[data-mg-bgvideo]');
    if (!holders.length || reduceMotion || (navigator.connection && navigator.connection.saveData)) return;
    const start = () => holders.forEach((holder) => {
      if (holder.dataset.ready) return;
      holder.dataset.ready = 'true';
      const { id } = holder.dataset;
      const begin = parseInt(holder.dataset.start, 10) || 0;
      const iframe = document.createElement('iframe');
      iframe.title = '';
      iframe.tabIndex = -1;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&disablekb=1&fs=0&modestbranding=1&playsinline=1&rel=0&iv_load_policy=3&cc_load_policy=0&enablejsapi=1&start=${begin}`;
      const fit = () => {
        const w = holder.clientWidth; const h = holder.clientHeight;
        const scale = Math.max(w / (h * 16 / 9), h / (w * 9 / 16)) * 1.32;
        iframe.style.width = `${(h * 16 / 9) * scale}px`;
        iframe.style.height = `${h * scale}px`;
      };
      fit();
      window.addEventListener('resize', fit, { passive: true });
      iframe.addEventListener('load', () => setTimeout(() => holder.classList.add('is-playing'), 4500));
      holder.appendChild(iframe);
    });
    if (document.readyState === 'complete') setTimeout(start, 600);
    else window.addEventListener('load', () => setTimeout(start, 600), { once: true });
  };

  /* ---------- Header section links: mark the section on screen ---------- */
  const initScrollSpy = () => {
    const links = [...document.querySelectorAll('[data-mp-spy]')];
    if (!links.length || !('IntersectionObserver' in window)) return;
    const targets = links.map((a) => document.querySelector(a.getAttribute('href'))).filter(Boolean);
    const onScreen = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => (entry.isIntersecting ? onScreen.add(entry.target) : onScreen.delete(entry.target)));
      const current = targets.filter((t) => onScreen.has(t)).pop();
      const id = current ? `#${current.id}` : null;
      links.forEach((a) => a.setAttribute('aria-current', String(a.getAttribute('href') === id)));
    }, { rootMargin: '-45% 0px -50% 0px' });
    targets.forEach((t) => io.observe(t));
  };

  /* ---------- Card details: open on tablet and desktop, folded on phones ---------- */
  const initMore = () => {
    const more = document.querySelectorAll('[data-mg-more]');
    if (!more.length) return;
    const wide = window.matchMedia('(min-width: 700px)');
    const apply = () => more.forEach((d) => { d.open = wide.matches; });
    apply();
    if (wide.addEventListener) wide.addEventListener('change', apply);
  };

  const initCart = () => {
    if (!document.querySelector('[data-mg-cart-count], [data-mg-bar][data-cart-label], mg-buy')) return;
    refreshCart();
    window.addEventListener('pageshow', (event) => { if (event.persisted) refreshCart(); });
  };

  /* ---------- Journey line: grows under the header, a dot per chapter fills as it is passed ---------- */
  const initProgress = () => {
    const track = document.querySelector('[data-mg-progress]');
    if (!track) return;
    const bar = track.querySelector('.mp-progress__bar');
    const links = [...document.querySelectorAll('.mp-header__nav a[data-mp-spy]')];
    const dots = links.map((a) => {
      const target = document.querySelector(a.getAttribute('href'));
      if (!target) return null;
      const dot = document.createElement('span');
      dot.className = 'mp-progress__dot';
      dot.title = a.textContent.trim();
      track.appendChild(dot);
      return { dot, target };
    }).filter(Boolean);
    let max = 1;
    const place = () => {
      max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      dots.forEach((d) => { d.at = Math.min(1, Math.max(0, (d.target.getBoundingClientRect().top + window.scrollY - window.innerHeight * 0.4) / max)); d.dot.style.left = `${d.at * 100}%`; });
    };
    const paint = () => {
      const p = Math.min(1, Math.max(0, window.scrollY / max));
      bar.style.transform = `scaleX(${p})`;
      dots.forEach((d) => d.dot.classList.toggle('is-passed', p >= d.at - 0.002));
    };
    place(); paint();
    let queued = false;
    window.addEventListener('scroll', () => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; paint(); }); }, { passive: true });
    window.addEventListener('resize', () => { place(); paint(); });
    window.addEventListener('load', () => { place(); paint(); });
  };

  // Google review button: on computers open Google's review form in a popup so visitors stay on the page.
  // Phones (and blocked popups) follow the normal link, which opens the Google Maps review sheet.
  const initReviewPopup = () => {
    document.querySelectorAll('[data-mg-review-popup]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (!window.matchMedia('(min-width: 990px) and (hover: hover)').matches) return;
        const w = 620;
        const h = Math.min(760, window.screen.availHeight - 80);
        const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
        const top = Math.round(window.screenY + Math.max(40, (window.outerHeight - h) / 2));
        const popup = window.open(link.href, 'mg-google-review', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
        if (popup) { event.preventDefault(); popup.focus(); }
      });
    });
  };

  // "Buy now" links that jump to a section with buy cards land on the cards themselves
  // (or the model switcher on phones), so the Buy buttons are in view, not just the heading.
  const initBuyJump = () => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link || link.getAttribute('href').length < 2) return;
      let section = null;
      try { section = document.querySelector(link.getAttribute('href')); } catch (err) { return; }
      const cards = section && section.querySelector('mg-switch, .mg-panels[data-layout="buybox"]');
      if (!cards) return;
      event.preventDefault();
      const header = document.querySelector('.mp-header');
      const offset = (header ? header.getBoundingClientRect().height : 0) + 16;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let top = cards.getBoundingClientRect().top + window.scrollY - offset;
      // If the cards are tall, go a little further so the first Buy button is fully on screen
      const firstBuy = [...section.querySelectorAll('.mg-panel__ctas, .mg-panel__range')].find((el) => el.offsetParent);
      // Where a model switcher is showing (phones, and tablets on pages that opt in), land on it so every model stays in reach.
      const switcher = section.querySelector('mg-switch');
      const switcherShown = switcher && switcher.offsetParent && getComputedStyle(switcher).display !== 'none';
      if (firstBuy && !switcherShown) {
        const buyBottom = firstBuy.getBoundingClientRect().bottom + window.scrollY;
        top = Math.max(top, buyBottom + 24 - window.innerHeight);
      }
      if (switcherShown) top = switcher.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' });
      if (history.replaceState) history.replaceState(null, '', link.getAttribute('href'));
    });
  };

  const boot = () => { init(); initNav(); initStickyOffset(); initSpecAccordions(); initScrollSpy(); initBgVideo(); initMore(); initCart(); initProgress(); initReviewPopup(); initBuyJump(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (event) => { init(event.target); initSpecAccordions(event.target); });
})();
